// src/components/Onboarding/TeamCard.js
import React, { useEffect, useState, useMemo } from 'react';
import checkmark from '../../images/checkmarkIcon.svg';

// icons
import facebookMessengerLogo from '../../images/facebookMessengerLogo.png';
import iMessageLogo from '../../images/iMessageLogo.png';
import WhatsAppLogo from '../../images/WhatsAppLogo.png';
import emailLogo from '../../images/emailLogo.svg'; // NEW

function useIsDesktop(minWidth = 768) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(min-width: ${minWidth}px)`).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(`(min-width: ${minWidth}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener ? mql.addEventListener('change', handler) : mql.addListener(handler);
    return () =>
      mql.removeEventListener
        ? mql.removeEventListener('change', handler)
        : mql.removeListener(handler);
  }, [minWidth]);
  return isDesktop;
}

const IconRender = ({ icon, alt = '', boxClass = 'h-6 w-6' }) => {
  // Fixed-size outer box (same for all icons)
  const Box = ({ children }) => (
    <span className={`${boxClass} inline-flex items-center justify-center shrink-0`}>
      {children}
    </span>
  );

  if (typeof icon === 'string') {
    // URL-based image (png/svg/webp, etc.)
    return (
      <Box>
        <img
          src={icon}
          alt={alt}
          className="h-full w-full max-h-full max-w-full object-contain"
          loading="lazy"
        />
      </Box>
    );
  }

  if (React.isValidElement(icon)) {
    // React element (e.g., <svg/>) — force-fit into the box
    return (
      <Box>
        {React.cloneElement(icon, {
          className: `h-full w-full ${icon.props.className || ''}`.trim(),
          'aria-hidden': icon.props['aria-hidden'] ?? 'true',
          focusable: 'false',
        })}
      </Box>
    );
  }

  // Fallback: emoji/text
  return (
    <Box>
      <span className="text-[18px] leading-none" aria-hidden="true">
        {icon}
      </span>
    </Box>
  );
};

function ButtonsRow({ buttons, equalThirds = false, onContactClick = null }) {
  const items = useMemo(() => {
    if (!buttons || buttons.length === 0) {
      return [
        { icon: facebookMessengerLogo, href: 'https://m.me/61572281754989', ariaLabel: 'FacebookMessenger', buttonType: 'messenger' },
        { icon: iMessageLogo, href: 'sms:+908503469160', ariaLabel: 'Messages', buttonType: 'imessage' },
        { icon: WhatsAppLogo, href: "https://wa.me/908503469160?text=Hi!%20I'm%20interested%20in%20your%20services.", ariaLabel: 'WhatsApp', buttonType: 'whatsapp' },
      ];
    }
    return buttons.slice(0, 3);
  }, [buttons]);

  const containerClass = equalThirds
    ? 'w-full grid grid-cols-3 gap-3'
    : 'w-full flex items-center justify-center gap-3';

  const handleButtonClick = (btn) => {
    if (onContactClick) {
      onContactClick({
        buttonType: btn.buttonType || btn.ariaLabel?.toLowerCase() || 'unknown',
        href: btn.href || null,
        ariaLabel: btn.ariaLabel,
      });
    }
  };

  return (
    <div className={containerClass}>
      {items.map((btn, i) => (
        <div key={i}>
          {btn.href ? (
            <a
              href={btn.href}
              aria-label={btn.ariaLabel || 'Contact'}
              className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 md:px-4 h-10 md:h-11 border border-white bg-mm_black hover:bg-mm_black/90 transition w-full shadow-sm"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleButtonClick(btn)}
            >
              <IconRender icon={btn.icon} alt={btn.ariaLabel} />
              <span className="text-xs md:text-sm font-medium text-white">Contact</span>
            </a>
          ) : (
            <button
              type="button"
              aria-label={btn.ariaLabel || 'Contact'}
              className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 md:px-4 h-10 md:h-11 border border-white bg-mm_black hover:bg-mm_black/90 transition w-full shadow-lg"
              onClick={() => {
                handleButtonClick(btn);
                if (btn.onClick) btn.onClick();
              }}
            >
              <IconRender icon={btn.icon} alt={btn.ariaLabel} />
              <span className="text-xs md:text-sm font-medium text-white">Contact</span>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Renders "Name  [email icon]"
// The first card's icon always emails enesyildirim@matchmedic.com,
// the second card's icon always emails alpmungan@matchmedic.com.
// For others, it falls back to the `email` prop if provided.
function NameWithEmail({ name, email, slot }) {
  if (!name) return null;
  const actualEmail =
    slot === 'first'
      ? 'enesyildirim@matchmedic.com'
      : slot === 'second'
      ? 'alpmungan@matchmedic.com'
      : email;

  return (
    <div className="flex items-center gap-2">
      <h3 className="text-base font-semibold text-white">{name}</h3>
      {actualEmail ? (
        <a
          href={`mailto:${actualEmail}`}
          aria-label={`Email ${name}`}
          title={`Email ${name}`}
          className="inline-flex items-center justify-center rounded focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-0 hover:opacity-90"
        >
          <IconRender icon={emailLogo} alt="Email" boxClass="h-4 w-4" />
        </a>
      ) : null}
    </div>
  );
}

function MobileLayout({ photoSrc, name, title, description, fact, buttons, email, slot, onContactClick }) {
  return (
    <div className="w-full rounded-2xl border border-white bg-gray-800 p-4 shadow-sm">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          {/* Fixed aspect ratio wrapper to normalize image display across different source intrinsic sizes */}
          <div className="w-full overflow-hidden rounded-xl" style={{ aspectRatio: '3 / 4' }}>
            <img
              src={photoSrc}
              alt={name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="col-span-2 flex flex-col items-start">
          <NameWithEmail name={name} email={email} slot={slot} />
          {title && <p className="text-xs text-gray-300 mb-1">{title}</p>}
          {description && <p className="text-sm mb-2 text-white">{description}</p>}
          {fact && (
            <div className="flex items-start gap-2">
              <img src={checkmark} alt="" className="h-4 w-4 mt-0.5" />
              <span className="text-sm text-white">{fact}</span>
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <ButtonsRow buttons={buttons} onContactClick={onContactClick} />
      </div>
    </div>
  );
}

function DesktopLayout({ photoSrc, name, title, description, fact, buttons, email, slot, onContactClick }) {
  return (
    <div className="w-full rounded-2xl border border-white bg-gray-900 shadow-sm p-4 md:p-5">
      <div className="grid grid-cols-12 gap-4 md:gap-6 items-start">
        <div className="col-span-4 flex justify-center">
          {/* Consistent aspect ratio + max width; object-cover ensures uniform cropping */}
          <div className="w-full max-w-[200px] overflow-hidden rounded-xl" style={{ aspectRatio: '3 / 4' }}>
            <img
              src={photoSrc}
              alt={name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
        <div className="col-span-8 flex flex-col">
          <div className="space-y-2">
            <NameWithEmail name={name} email={email} slot={slot} />
            {title && <p className="text-sm text-gray-300">{title}</p>}
            {description && (
              <p className="text-sm text-white border-b pb-2 border-white/20">{description}</p>
            )}
            {fact && (
              <div className="flex items-start gap-2">
                <img src={checkmark} alt="" className="h-4 w-4 mt-0.5" />
                <span className="text-sm text-white">{fact}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Full-width buttons row spanning the entire card width on desktop */}
      <div className="mt-4 text-white">
        <ButtonsRow buttons={buttons} equalThirds onContactClick={onContactClick} />
      </div>
    </div>
  );
}

export default function TeamCard(props) {
  const { desktopMinWidth = 768, ...rest } = props;
  const isDesktop = useIsDesktop(desktopMinWidth);
  return isDesktop ? <DesktopLayout {...rest} /> : <MobileLayout {...rest} />;
}
